import { privacyPolicyContactEmail } from '@/utils/privacy-policy';

export const siteName = 'Budgy';
export const siteUrl = `https://${import.meta.env.VITE_HOSTNAME ?? 'getbudgy.co.uk'}`;
export const defaultOgImagePath = '/og-image.png';
export const isIndexable = import.meta.env.MODE === 'production';
const organizationId = `${siteUrl}/#organization`;

const organizationLogoSize = 180;

export const buildRobotsContent = ({ isNoindex = false } = {}) => {
  if (!isIndexable) {
    return 'noindex, nofollow';
  }
  return isNoindex ? 'noindex, follow' : 'index, follow';
};

type PageMetaOptions = {
  title: string;
  description: string;
  path: string;
  image?: string | null;
  imageAlt?: string;
  type?: 'article' | 'website';
};

export const absoluteUrl = (path: string) => new URL(path, siteUrl).toString();

export const buildOrganizationSchema = () => ({
  '@type': 'Organization',
  '@id': organizationId,
  name: siteName,
  url: siteUrl,
  logo: {
    '@type': 'ImageObject',
    url: absoluteUrl('/apple-touch-icon.png'),
    width: organizationLogoSize,
    height: organizationLogoSize,
  },
  contactPoint: {
    '@type': 'ContactPoint',
    email: privacyPolicyContactEmail,
    contactType: 'customer support',
    areaServed: 'GB',
    availableLanguage: 'en-GB',
  },
});

export const buildBreadcrumbSchema = (
  items: readonly { name: string; path: string }[],
) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: absoluteUrl(item.path),
  })),
});

export const buildStructuredDataScript = (data: unknown) => ({
  type: 'application/ld+json',
  children: JSON.stringify(data).replaceAll('<', '\\u003c'),
});

export type LandingFaqItem = {
  question: string;
  answer: string;
};

export const buildFaqPageSchema = (faqs: readonly LandingFaqItem[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: { '@type': 'Answer', text: answer },
  })),
});

export const buildBreadcrumbListSchema = (
  items: readonly { name: string; url: string }[],
) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map(({ name, url }, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name,
    item: url,
  })),
});

const minimumItemListSize = 2;

const buildItemListSchema = (urls: readonly string[]) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  numberOfItems: urls.length,
  itemListElement: urls.map((url, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    url,
  })),
});

export const itemListStructuredData = (urls: readonly string[]) =>
  urls.length < minimumItemListSize ? null : buildItemListSchema(urls);

export const buildRealEstateAgentSchema = ({
  name,
  url,
  address,
  telephone,
  website,
  googleMapsUrl,
}: {
  name: string;
  url: string;
  address?: string | null;
  telephone?: string | null;
  website?: string | null;
  googleMapsUrl?: string | null;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'RealEstateAgent',
  name,
  url,
  ...(address ? { address } : {}),
  ...(telephone ? { telephone } : {}),
  ...(website ? { sameAs: [website] } : {}),
  ...(googleMapsUrl ? { hasMap: googleMapsUrl } : {}),
});

export const buildPageMeta = ({
  title,
  description,
  path,
  image,
  imageAlt,
  type = 'website',
}: PageMetaOptions) => {
  const canonicalUrl = absoluteUrl(path);
  const imageUrl = image ?? absoluteUrl(defaultOgImagePath);
  const imageAltText =
    imageAlt ?? 'Budgy — Is your letting agent still the best deal?';

  return {
    meta: [
      { title },
      { name: 'description', content: description },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: canonicalUrl },
      { property: 'og:type', content: type },
      { property: 'og:image', content: imageUrl },
      { property: 'og:image:alt', content: imageAltText },
      { property: 'og:site_name', content: siteName },
      { property: 'og:locale', content: 'en_GB' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: imageUrl },
      { name: 'twitter:image:alt', content: imageAltText },
    ],
    links: [{ rel: 'canonical', href: canonicalUrl }],
  };
};
